/**
 * Typed wrappers for the G15 travel pre-approval module.
 *
 * Routes (taken VERBATIM from backend/internal/handlers/travel_preapprovals.go
 * and backend/internal/router/router.go):
 *
 *   POST   /api/v1/travel-preapprovals             — employee submits
 *   GET    /api/v1/travel-preapprovals             — role-filtered list
 *   GET    /api/v1/travel-preapprovals/{id}        — single (access-scoped)
 *   POST   /api/v1/travel-preapprovals/{id}/approve — manager-of-requester OR HR
 *   POST   /api/v1/travel-preapprovals/{id}/reject  — manager-of-requester OR HR
 *   POST   /api/v1/travel-preapprovals/{id}/cancel  — requester (pending only)
 *
 * SECURITY NOTES:
 *  - No client-supplied tenant_id / requester_id is ever sent: the server
 *    derives both from the session. The list is role-FILTERED in SQL
 *    (employee=own, manager=own+reports, HR=all) — that SQL scope is the
 *    real boundary, NOT a query param.
 *  - Approve/Reject re-check (manager-of-requester OR HR; never the
 *    requester themselves) at the handler layer; UI gates are presentation
 *    mirrors only.
 *  - Cancel: requester-self-only at the handler layer + the SQL
 *    conditional UPDATE (defence in depth) — a non-requester cannot
 *    cancel someone else's preapproval.
 *  - Money is integer paise on the wire — never parsed as a float here.
 *  - The 404-shape on an out-of-scope/cross-tenant id preserves the
 *    no-existence-oracle property.
 */

import { api } from "./client";

export const TRAVEL_PREAPPROVALS_BASE = "/api/v1/travel-preapprovals";

// ---------------------------------------------------------------------------
// Wire types — mirror handlers.travelPreapprovalDTO EXACTLY
// ---------------------------------------------------------------------------

// Lifecycle status. Mirrors database.TravelPreapprovalStatus + the DB CHECK
// constraint in migration 045 (pending/approved/rejected/cancelled/exhausted).
// `exhausted` is automatic — set in the same TX as an expense INSERT when
// consumed_budget_paise reaches approved_budget_paise; the wire returns it
// on a fetched row but the UI never transitions a row INTO `exhausted`.
export type TravelPreapprovalStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "cancelled"
  | "exhausted";

export interface TravelPreapproval {
  id: number;
  requester_id: number;
  title: string;
  purpose: string;
  destination: string;
  /** YYYY-MM-DD (date-only). */
  start_date: string;
  /** YYYY-MM-DD (date-only). */
  end_date: string;
  estimated_budget_paise: number;
  /** Free-form JSONB the engine never reads — opaque to the UI. */
  estimated_budget_breakdown?: unknown;
  status: TravelPreapprovalStatus;
  approver_id?: number;
  decision_notes?: string;
  /** RFC3339. */
  decided_at?: string;
  /** Set only on `status='approved'` / `exhausted`. */
  approved_budget_paise?: number;
  /** Running tally of expenses charged against this cap. */
  consumed_budget_paise: number;
  /** RFC3339. */
  created_at: string;
  updated_at: string;
  /**
   * Single GET only (lifted from the response envelope by
   * getTravelPreapproval): the caller owns this pending request and is the
   * workspace's only possible approver — they may approve it themselves
   * with { self_approve: true } (explicit, audited).
   */
  can_self_approve?: boolean;
}

export interface ListTravelPreapprovalsResponse {
  items: TravelPreapproval[];
}

export interface TravelPreapprovalResponse {
  preapproval: TravelPreapproval;
  /** Single GET only — see TravelPreapproval.can_self_approve. */
  can_self_approve?: boolean;
}

// ---------------------------------------------------------------------------
// Request bodies
// ---------------------------------------------------------------------------

export interface CreateTravelPreapprovalRequest {
  title: string;
  purpose: string;
  destination: string;
  /** YYYY-MM-DD. */
  start_date: string;
  /** YYYY-MM-DD. End must be >= start (server validates 422). */
  end_date: string;
  /** Positive integer paise (server validates > 0). */
  estimated_budget_paise: number;
  /** Optional free-form JSONB (flight/hotel/per_diem keys). */
  estimated_budget_breakdown?: unknown;
}

export interface ApproveTravelPreapprovalRequest {
  /** The CAP (positive integer paise; may differ from the estimate). */
  approved_budget_paise: number;
  /** Optional note shared with the requester. */
  decision_notes?: string;
  /** Solo-admin self-approval opt-in (the requester approving their own). */
  self_approve?: boolean;
}

export interface RejectTravelPreapprovalRequest {
  /** REQUIRED — the server validates non-empty. */
  decision_notes: string;
}

// ---------------------------------------------------------------------------
// List — role-filtered server-side
// ---------------------------------------------------------------------------

// GET /api/v1/travel-preapprovals[?status=][&exclude_self=1]
// The handler accepts an optional status filter (one of the five
// TravelPreapprovalStatus values); none widens the SQL scope.
// `exclude_self=1` narrows an approver's list to requests they can ACT
// on (own requests removed).
export async function listTravelPreapprovals(
  params?: { status?: TravelPreapprovalStatus; exclude_self?: boolean },
  signal?: AbortSignal,
): Promise<ListTravelPreapprovalsResponse> {
  const { exclude_self, ...rest } = params ?? {};
  const { data } = await api.get<ListTravelPreapprovalsResponse>(
    TRAVEL_PREAPPROVALS_BASE,
    {
      params: { ...rest, ...(exclude_self ? { exclude_self: 1 } : {}) },
      signal,
    },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Get one — access-scoped (out-of-scope → 404, never 403)
// ---------------------------------------------------------------------------

export async function getTravelPreapproval(
  id: number,
  signal?: AbortSignal,
): Promise<TravelPreapproval> {
  const { data } = await api.get<TravelPreapprovalResponse>(
    `${TRAVEL_PREAPPROVALS_BASE}/${id}`,
    { signal },
  );
  return { ...data.preapproval, can_self_approve: data.can_self_approve === true };
}

// ---------------------------------------------------------------------------
// Create — own submission (server derives requester_id from session)
// ---------------------------------------------------------------------------

export async function createTravelPreapproval(
  body: CreateTravelPreapprovalRequest,
): Promise<TravelPreapproval> {
  const { data } = await api.post<TravelPreapprovalResponse>(
    TRAVEL_PREAPPROVALS_BASE,
    body,
  );
  return data.preapproval;
}

// ---------------------------------------------------------------------------
// Approve / Reject — manager-of-requester OR HR; never own request
// ---------------------------------------------------------------------------

export async function approveTravelPreapproval(
  id: number,
  body: ApproveTravelPreapprovalRequest,
): Promise<TravelPreapproval> {
  const { data } = await api.post<TravelPreapprovalResponse>(
    `${TRAVEL_PREAPPROVALS_BASE}/${id}/approve`,
    body,
  );
  return data.preapproval;
}

export async function rejectTravelPreapproval(
  id: number,
  body: RejectTravelPreapprovalRequest,
): Promise<TravelPreapproval> {
  const { data } = await api.post<TravelPreapprovalResponse>(
    `${TRAVEL_PREAPPROVALS_BASE}/${id}/reject`,
    body,
  );
  return data.preapproval;
}

// ---------------------------------------------------------------------------
// Cancel — requester-self-only, pending only
// ---------------------------------------------------------------------------

export async function cancelTravelPreapproval(
  id: number,
): Promise<TravelPreapproval> {
  const { data } = await api.post<TravelPreapprovalResponse>(
    `${TRAVEL_PREAPPROVALS_BASE}/${id}/cancel`,
    {},
  );
  return data.preapproval;
}
